'use client';

/**
 * DetailRow — the LABEL / value spec row used inside detail modals.
 *
 * Left column: tiny mono-caps label in ink-2.
 * Right column: display-caps value in ink, right-aligned, max 65%
 * of the row width so long values wrap into a second line instead of
 * overflowing the modal.
 *
 * The bottom hairline divider is drawn via Tailwind classes (NOT
 * inline style). This is load-bearing: build 1276 (2026-05-10) found
 * that inline `borderBottomWidth: 1` beats the Tailwind `:last-child`
 * rule, leaving the last row visibly thicker than the rest. Keeping
 * the border on the Tailwind class lets `last:border-b-0` zero it out
 * on the final row in a stack.
 *
 * Used in YarnDetailModal, NeedleDetailModal, and
 * ProjectNeedleDetailModal — each of which used to define this same
 * shape as a local `function Row({label, value})`. Build 1358 hoisted
 * it here so a future restyle is one component change instead of three.
 */

import type { ReactNode } from 'react';

export type DetailRowProps = {
  label: string;
  /**
   * Optional small caption below the label. Use it for units or
   * qualifiers that would otherwise bloat the label and push the
   * value column into wraps (e.g. "per 10cm" under "MANUFACTURER'S
   * GAUGE"). Same mono caps treatment one step smaller and lighter
   * than the main label.
   */
  sublabel?: string;
  /**
   * The value can be a plain string OR rich content (e.g. with an
   * embedded `<wbr>` for soft-wrap hints). The modals used to render
   * with `dangerouslySetInnerHTML` for `<wbr>` injection — supporting
   * `ReactNode` here keeps that flexibility without the dangerous API.
   */
  value: ReactNode;
};

export function DetailRow({ label, sublabel, value }: DetailRowProps) {
  return (
    <div
      className="border-b border-b-ink last:border-b-0"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 10,
        padding: '8px 12px',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--ink-2)',
            lineHeight: 1.15,
          }}
        >
          {label}
        </span>
        {sublabel && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 8.5,
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              lineHeight: 1.15,
            }}
          >
            {sublabel}
          </span>
        )}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '-0.005em',
          textTransform: 'uppercase',
          color: 'var(--ink)',
          textAlign: 'right',
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </span>
    </div>
  );
}
